import type { BalanceData, AprData, LenderConfigMap, PostTradeMetrics } from '../utils'
import { EMPTY_BALANCE, getAssetConfig, computePostTradeMetrics } from '../utils'
import type { LenderYields } from '../../../../types/lenderTypes'
import { getOrganicYieldDeposit, getRewardYieldDeposit, getIntrinsicYieldDeposit } from '../yield'

/**
 * Compute the post-trade balance metrics for a deposit operation.
 *
 * A deposit increases collateral, borrowDiscountedCollateral, deposits, and nav.
 * When `apr` and `yieldParams` are provided, also computes `aprDataPost`.
 */
export function computeDepositDelta(
  amount: number,
  price: number,
  balanceData: BalanceData,
  config: LenderConfigMap,
  modeId: string,
  createNewSubAccount = false,
  apr?: AprData,
  yieldParams?: LenderYields,
): PostTradeMetrics {
  const { borrowCollateralFactor, collateralFactor } = getAssetConfig(config, modeId)
  const base = createNewSubAccount ? EMPTY_BALANCE : balanceData

  const dollarAmount = amount * price
  const postTrade: BalanceData = {
    ...base,
    borrowDiscountedCollateral:
      base.borrowDiscountedCollateral + borrowCollateralFactor * dollarAmount,
    collateral: base.collateral + collateralFactor * dollarAmount,
    deposits: base.deposits + dollarAmount,
    nav: base.nav + dollarAmount,
  }

  const result = computePostTradeMetrics(base, postTrade)

  if (apr && yieldParams) {
    const cashFlowDeposits = base.deposits * apr.depositApr
    const cashFlowDebt = base.debt * apr.borrowApr
    const rewardFlowDeposits = base.deposits * (apr.rewardDepositApr ?? 0)
    const rewardFlowDebt = base.debt * (apr.rewardBorrowApr ?? 0)
    const intrinsicFlowDeposits = base.deposits * apr.intrinsicDepositApr
    const intrinsicFlowDebt = base.debt * apr.intrinsicBorrowApr

    const newApr = { ...apr }
    ;[newApr.borrowApr, newApr.depositApr, newApr.apr] = getOrganicYieldDeposit(
      dollarAmount, cashFlowDeposits, cashFlowDebt, base.deposits, base.debt, yieldParams
    )
    newApr.rewards = getRewardYieldDeposit(
      dollarAmount, rewardFlowDeposits, rewardFlowDebt, base.deposits, base.debt, yieldParams
    )
    ;[newApr.intrinsicBorrowApr, newApr.intrinsicDepositApr, newApr.intrinsicApr] =
      getIntrinsicYieldDeposit(
        dollarAmount, intrinsicFlowDeposits, intrinsicFlowDebt, base.deposits, base.debt, yieldParams
      )
    result.post.aprData = newApr
  }

  return result
}
